<template>
  <div class="tw-p-2">
    <!-- Back -->
    <div class="tw-mb-4">
      <a-button intent="light" left-icon="solar:arrow-left-linear" @click="goBack">
        {{ $t('videos.back-to-library', 'Back to library') }}
      </a-button>
    </div>

    <div v-if="loading" class="tw-py-20 tw-text-center tw-text-gray-400">
      <icon icon="eos-icons:three-dots-loading" class="tw-text-4xl" />
    </div>

    <div v-else-if="video" class="tw-grid tw-grid-cols-1 xl:tw-grid-cols-12 tw-gap-6">
      <div class="xl:tw-col-span-8 tw-space-y-4">
        <VideoPlayer :video="video" />
        <div class="tw-bg-white tw-border tw-border-gray-100 tw-rounded-lg tw-p-6">
          <h1 class="tw-text-xl tw-font-bold tw-text-gray-900">{{ video.title }}</h1>
          <div class="tw-flex tw-flex-wrap tw-gap-2 tw-mt-3">
            <span v-for="c in video.categories" :key="c.id" class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-600 tw-text-xs">{{ c.name }}</span>
          </div>
          <p class="tw-text-gray-600 tw-mt-4 tw-whitespace-pre-line">{{ video.description }}</p>
        </div>
      </div>

      <!-- Related -->
      <div class="xl:tw-col-span-4 tw-space-y-3">
        <h2 class="tw-text-sm tw-font-bold tw-text-gray-500 tw-uppercase">{{ $t('videos.more-videos', 'More videos') }}</h2>
        <router-link
          v-for="item in related"
          :key="item.id"
          :to="{ name: 'shared.foodshow-on-demand.watch', params: { id: item.id } }"
          class="tw-flex tw-gap-3 tw-group tw-bg-white tw-rounded-lg tw-p-2 tw-border tw-border-gray-100 hover:tw-shadow"
        >
          <div class="tw-w-32 tw-aspect-video tw-bg-gray-100 tw-rounded tw-overflow-hidden tw-shrink-0 tw-relative">
            <img v-if="item.thumbnail_url" :src="item.thumbnail_url" class="tw-w-full tw-h-full tw-object-cover" alt="" />
            <div v-else class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center"><icon icon="solar:videocamera-record-bold-duotone" class="tw-text-2xl tw-text-gray-300" /></div>
            <span v-if="item.duration" class="tw-absolute tw-bottom-1 tw-right-1 tw-bg-black/70 tw-text-white tw-text-[10px] tw-px-1 tw-rounded">{{ item.duration }}</span>
          </div>
          <div class="tw-min-w-0 tw-flex-1">
            <p class="tw-text-sm tw-font-semibold tw-text-gray-800 tw-line-clamp-2 group-hover:tw-text-primary-600">{{ item.title }}</p>
            <p v-if="item.description" class="tw-text-xs tw-text-gray-500 tw-line-clamp-2 tw-mt-1">{{ item.description }}</p>
            <div v-if="item.categories && item.categories.length" class="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1">
              <span v-for="c in item.categories.slice(0,2)" :key="c.id" class="tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-gray-500 tw-text-[10px]">{{ c.name }}</span>
            </div>
          </div>
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import VideoPlayer from '@/components/video/VideoPlayer.vue';
import videosApi from '@/api/videos';
import useAlert from '@/composables/useAlert';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const loading = ref(true);
const video = ref(null);
const related = ref([]);

const goBack = () => router.push({ name: 'shared.foodshow-on-demand' });

const fetchVideo = async () => {
    loading.value = true;
    try {
        const response = await videosApi.get(route.params.id);
        video.value = response.data?.data || null;
    } catch (error) {
        console.error('Error loading video:', error);
        useAlert(t('videos.not-found', 'Video not found'), 'danger');
        goBack();
    } finally {
        loading.value = false;
    }
};

const fetchRelated = async () => {
    try {
        const response = await videosApi.paginate({ per_page: 6 });
        const list = response.data?.data?.data || [];
        related.value = list.filter(v => String(v.id) !== String(route.params.id)).slice(0, 5);
    } catch (error) {
        console.error('Error loading related videos:', error);
    }
};

const load = () => { fetchVideo(); fetchRelated(); };

watch(() => route.params.id, load);
onMounted(load);
</script>
